import { useEffect, useState } from "react";
import { ArrowsClockwise, CalendarBlank, CircleNotch, Warning, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtBRL, fmtDate, todayISO } from "../lib/format";
import { DatePicker } from "./DatePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const KIND_STYLE = {
  aberto: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  pago: "text-emerald-200 border-emerald-400/50 bg-emerald-400/15",
  baixado: "text-zinc-400 border-zinc-500/40 bg-zinc-500/10",
  pendente: "text-sky-300 border-sky-500/40 bg-sky-500/10",
  rejeitado: "text-rose-300 border-rose-500/40 bg-rose-500/10",
  bloqueado: "text-rose-300 border-rose-500/40 bg-rose-500/10",
  desconhecido: "text-zinc-400 border-zinc-600/40 bg-zinc-600/10",
};

const ago = (iso) => {
  if (!iso) return "";
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 1) return "agora";
  if (m < 60) return `há ${m} min`;
  const h = Math.round(m / 60);
  if (h < 48) return `há ${h} h`;
  return `há ${Math.round(h / 24)} d`;
};

// Situação vigente no Sicredi (última consulta) — mostrada na linha do boleto
export const SicrediSituacao = ({ b, onRefresh, busy }) => {
  if (b.simulado) return <span data-testid={`boleto-situacao-${b.code}`} className="block text-[10px] text-amber-400/80 mt-0.5">simulação — sem consulta ao Sicredi</span>;
  const kind = b.situacao_kind || (b.situacao_label ? "aberto" : "desconhecido");
  return (
    <span className="flex items-center gap-1 mt-1">
      <span data-testid={`boleto-situacao-${b.code}`} title={b.sicredi_situacao ? `Sicredi: ${b.sicredi_situacao}` : "Ainda não consultado no Sicredi"}
        className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 border rounded ${KIND_STYLE[kind]}`}>
        Sicredi: {b.situacao_label || "não consultado"}
      </span>
      {b.last_check_at && <span className="text-[10px] text-zinc-500" data-testid={`boleto-situacao-ago-${b.code}`}>{ago(b.last_check_at)}</span>}
      {onRefresh && b.status === "REGISTRADO" && (
        <button type="button" data-testid={`boleto-situacao-refresh-${b.code}`} disabled={busy} onClick={onRefresh} title="Consultar situação agora no Sicredi"
          className="text-zinc-500 hover:text-sky-300 disabled:opacity-50 transition-colors"><ArrowsClockwise size={12} className={busy ? "animate-spin" : ""} /></button>
      )}
    </span>
  );
};

// Consulta ao vivo antes de baixar/alterar: devolve {situacao, boleto} ou null (simulado)
const useLiveCheck = (b, open) => {
  const [state, setState] = useState({ loading: false, situacao: null, boleto: null, error: "" });
  useEffect(() => {
    if (!open || !b) return;
    if (b.simulado) { setState({ loading: false, situacao: null, boleto: b, error: "" }); return; }
    setState({ loading: true, situacao: null, boleto: b, error: "" });
    api.post(`/boletos/${b.id}/verificar`)
      .then(({ data }) => setState({ loading: false, situacao: data.situacao || null, boleto: data.boleto || b, error: data.checked ? "" : data.message || "" }))
      .catch((e) => setState({ loading: false, situacao: null, boleto: b, error: apiError(e) }));
  }, [b?.id, open]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
};

const LivePanel = ({ b, live, permission }) => {
  if (b.simulado) return <p data-testid="live-sim" className="text-xs text-amber-300 border border-amber-500/30 bg-amber-500/10 rounded px-3 py-2">Boleto de simulação — a operação é apenas local.</p>;
  if (live.loading) return <p data-testid="live-loading" className="text-xs text-zinc-400 flex items-center gap-2"><CircleNotch size={14} className="animate-spin" /> Consultando a situação atual no Sicredi...</p>;
  if (live.error) return <p data-testid="live-error" className="text-xs text-rose-300 flex items-start gap-1.5"><Warning size={14} className="mt-0.5 shrink-0" /> Não foi possível consultar o Sicredi agora: {live.error}</p>;
  const s = live.situacao;
  if (!s) return null;
  const ok = s[permission];
  return (
    <div data-testid="live-situacao" className={`text-xs rounded px-3 py-2 border ${ok ? "border-emerald-500/30 bg-emerald-500/5" : "border-rose-500/40 bg-rose-500/10"}`}>
      <p className="text-zinc-300">Situação no Sicredi agora: <b data-testid="live-situacao-label" className={ok ? "text-emerald-300" : "text-rose-300"}>{s.label}</b>
        {s.due_date && <span className="text-zinc-500"> · vencimento no banco {fmtDate(s.due_date)}</span>}</p>
      {!ok && <p data-testid="live-situacao-block" className="mt-1 text-rose-200">
        {s.kind === "pago" ? "O boleto já foi PAGO — não pode ser cancelado nem alterado. O sistema marcou como liquidado; confirme a baixa da parcela."
          : s.kind === "baixado" ? "O boleto já está BAIXADO no Sicredi — nada a fazer; o status local foi atualizado."
          : s.kind === "pendente" ? "O Sicredi ainda está confirmando o REGISTRO deste boleto — aguarde alguns minutos e tente de novo."
          : s.kind === "rejeitado" ? "O registro foi REJEITADO pelo Sicredi — o boleto não existe no banco. Baixe-o aqui e emita outro."
          : "O boleto está em protesto/negativação — o Sicredi não permite esta operação via API; procure a cooperativa."}
      </p>}
    </div>
  );
};

export const BoletoBaixaDialog = ({ boleto: b, onClose, onDone }) => {
  const open = !!b;
  const live = useLiveCheck(b, open);
  const [busy, setBusy] = useState(false);
  const allowed = !!b && !live.loading && (b.simulado || !!live.error || !live.situacao || live.situacao.can_baixar);
  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/boletos/${b.id}/baixar`);
      toast.success(data.message, { duration: 9000 });
      onDone?.(data);
      onClose();
    } catch (e) {
      toast.error(apiError(e), { duration: 12000 });
      onDone?.();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md" data-testid="boleto-baixa-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><XCircle size={20} className="text-rose-400" /> Cancelar (baixar) boleto #{b?.code}</DialogTitle></DialogHeader>
        {b && (
          <div className="space-y-3 text-sm">
            <p className="text-zinc-300">{b.client_name} · <b>{fmtBRL(b.amount)}</b> · vence {fmtDate(b.due_date)}{b.seu_numero ? <span className="text-zinc-500"> · ref. {b.seu_numero}</span> : null}</p>
            <LivePanel b={b} live={live} permission="can_baixar" />
            <p className="text-xs text-zinc-500">A parcela no Financeiro continua em aberto — você pode emitir um novo boleto para ela depois.</p>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" data-testid="boleto-baixa-cancel" onClick={onClose} className="text-sm text-zinc-400 hover:text-zinc-200 px-3 py-2">Voltar</button>
              <button type="button" data-testid="boleto-baixa-confirm" disabled={busy || !allowed} onClick={confirm}
                className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded transition-colors">
                {busy ? <CircleNotch size={15} className="animate-spin" /> : <XCircle size={15} />} Confirmar baixa
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const BoletoVencimentoDialog = ({ boleto: b, onClose, onDone }) => {
  const open = !!b;
  const live = useLiveCheck(b, open);
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (b) setDue(b.due_date); }, [b]);
  const today = todayISO();
  const problem = !due ? "Informe a nova data" : due < today ? "O Sicredi não aceita vencimento anterior a hoje" : due === b?.due_date ? "Escolha uma data diferente da atual" : "";
  const allowed = !!b && !problem && !live.loading && (b.simulado || !!live.error || !live.situacao || live.situacao.can_alterar);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/boletos/${b.id}/vencimento`, { due_date: due });
      toast.success(data.message, { duration: 9000 });
      onDone?.(data);
      onClose();
    } catch (err) {
      toast.error(apiError(err), { duration: 12000 });
      onDone?.();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md" data-testid="boleto-vencimento-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><CalendarBlank size={20} className="text-indigo-400" /> Alterar vencimento — boleto #{b?.code}</DialogTitle></DialogHeader>
        {b && (
          <form onSubmit={submit} className="space-y-3 text-sm">
            <p className="text-zinc-300">{b.client_name} · <b>{fmtBRL(b.amount)}</b> · vencimento atual <b data-testid="vencimento-atual">{fmtDate(b.due_date)}</b>
              {b.due_date_original && b.due_date_original !== b.due_date && <span className="text-zinc-500"> (original {fmtDate(b.due_date_original)})</span>}</p>
            <LivePanel b={b} live={live} permission="can_alterar" />
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-1">Novo vencimento</label>
              <DatePicker value={due} onChange={setDue} testId="vencimento-novo" minDate={today} />
              <p data-testid="vencimento-hint" className={`text-[11px] mt-1 ${problem ? "text-rose-300" : "text-zinc-500"}`}>
                {problem || "O Sicredi registra a nova data no próximo processamento; o boleto já impresso continua válido e a parcela no Financeiro é atualizada."}
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" data-testid="vencimento-cancel" onClick={onClose} className="text-sm text-zinc-400 hover:text-zinc-200 px-3 py-2">Voltar</button>
              <button type="submit" data-testid="vencimento-confirm" disabled={busy || !allowed}
                className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded transition-colors">
                {busy ? <CircleNotch size={15} className="animate-spin" /> : <CalendarBlank size={15} />} Alterar vencimento
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
